<script setup>
import { reactive, computed, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import { useTemplateVariableMapping } from 'dashboard/composables/useTemplateVariableMapping';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getWhatsAppInboxes'),
  getFilteredWhatsAppTemplates: useMapGetter(
    'inboxes/getFilteredWhatsAppTemplates'
  ),
  getFilteredTwilioTemplates: useMapGetter(
    'inboxes/getFilteredTwilioTemplates'
  ),
  contactAttributes: useMapGetter('attributes/getContactAttributes'),
};

const initialState = {
  title: '',
  inboxId: null,
  templateId: null,
  scheduledAt: null,
  selectedAudience: [],
};

const state = reactive({ ...initialState });
const templateParserRef = ref(null);
const templateVariableMappings = ref({});
const variableSelections = ref({});

const rules = {
  title: { required, minLength: minLength(1) },
  inboxId: { required },
  templateId: { required },
  scheduledAt: { required },
  selectedAudience: { required },
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  // Added to disable the scheduled at field from being set to the current time
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const selectedInbox = computed(() => {
  if (!state.inboxId) return null;
  return formState.inboxes.value.find(inbox => inbox.id === state.inboxId);
});

const isTwilioWhatsAppInbox = computed(() => {
  const inbox = selectedInbox.value;
  const result =
    inbox &&
    inbox.channel_type === 'Channel::TwilioSms' &&
    inbox.medium === 'whatsapp';
  return result;
});

const templateOptions = computed(() => {
  if (!state.inboxId) return [];

  // Use appropriate template getter based on inbox type
  const templates = isTwilioWhatsAppInbox.value
    ? formState.getFilteredTwilioTemplates.value(state.inboxId)
    : formState.getFilteredWhatsAppTemplates.value(state.inboxId);

  return templates.map(template => {
    // Create a more user-friendly label from template name
    const templateName =
      template.friendly_name || template.name || 'Unnamed Template';
    const friendlyName = templateName
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

    return {
      value: template.content_sid || template.id,
      label: friendlyName,
      template: template,
    };
  });
});

const selectedTemplate = computed(() => {
  if (!state.templateId) return null;
  return templateOptions.value.find(option => option.value === state.templateId)
    ?.template;
});

const {
  variableMappingOptions,
  extractTemplateVariables,
  isStaticMode: _isStaticMode,
  handleVariableSelect: _handleVariableSelect,
  getPreviewContent: _getPreviewContent,
} = useTemplateVariableMapping({
  i18nBaseKey: 'CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.VARIABLE_OPTIONS',
  contactAttributes: formState.contactAttributes,
});

const templateVariables = computed(() => {
  if (!selectedTemplate.value || !isTwilioWhatsAppInbox.value) return [];
  const body =
    selectedTemplate.value.body || selectedTemplate.value.content || '';
  return extractTemplateVariables(body);
});

const isStaticMode = varKey => _isStaticMode(variableSelections.value, varKey);

const handleVariableSelect = (varKey, val) =>
  _handleVariableSelect(
    variableSelections,
    templateVariableMappings,
    varKey,
    val
  );

const previewContent = computed(() => {
  const template = selectedTemplate.value;
  if (!template) return '';
  const content = template.body || template.content || '';
  return _getPreviewContent(
    content,
    variableSelections.value || {},
    templateVariableMappings.value || {}
  );
});

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.WHATSAPP.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  template: getErrorMessage('templateId', 'TEMPLATE'),
  scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
  audience: getErrorMessage('selectedAudience', 'AUDIENCE'),
}));

const hasRequiredTemplateParams = computed(() => {
  return templateParserRef.value?.v$?.$invalid === false || true;
});

const isSubmitDisabled = computed(
  () => v$.value.$invalid || !hasRequiredTemplateParams.value
);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const resetState = () => {
  Object.assign(state, initialState);
  v$.value.$reset();
};

const handleCancel = () => emit('cancel');

const prepareCampaignDetails = () => {
  // Find the selected template to get its content
  const currentTemplate = selectedTemplate.value;
  const parserData = templateParserRef.value;

  let templateContent = '';
  let templateParams = {};

  if (isTwilioWhatsAppInbox.value) {
    // For Twilio templates, use the template body/content directly
    templateContent = currentTemplate?.body || currentTemplate?.content || '';
    templateParams = {
      name: currentTemplate?.friendly_name || currentTemplate?.name || '',
      sid:
        currentTemplate?.content_sid ||
        currentTemplate?.sid ||
        currentTemplate?.id,
      language: currentTemplate?.language || 'en',
      category: currentTemplate?.category || 'UTILITY',
      processed_params: { ...templateVariableMappings.value },
    };
  } else {
    // For regular WhatsApp templates, use the parser
    templateContent = parserData?.renderedTemplate || '';
    templateParams = {
      name: currentTemplate?.name || '',
      namespace: currentTemplate?.namespace || '',
      category: currentTemplate?.category || 'UTILITY',
      language: currentTemplate?.language || 'en_US',
      processed_params: parserData?.processedParams || {},
    };
  }

  return {
    title: state.title,
    message: templateContent,
    template_params: templateParams,
    inbox_id: state.inboxId,
    scheduled_at: formatToUTCString(state.scheduledAt),
    audience: state.selectedAudience?.map(id => ({
      id,
      type: 'Label',
    })),
  };
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  emit('submit', prepareCampaignDetails());
  resetState();
  handleCancel();
};

// Reset template selection when inbox changes
watch(
  () => state.inboxId,
  () => {
    state.templateId = null;
  }
);

// Reset variable mappings when template changes
watch(
  () => state.templateId,
  () => {
    templateVariableMappings.value = {};
    variableSelections.value = {};
  }
);
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label for="template" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL') }}
      </label>
      <ComboBox
        id="template"
        v-model="state.templateId"
        :options="templateOptions"
        :has-error="!!formErrors.template"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.PLACEHOLDER')"
        :message="formErrors.template"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
      <p class="mt-1 text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.INFO') }}
      </p>
    </div>

    <!-- Template Parser -->
    <WhatsAppTemplateParser
      v-if="selectedTemplate && !isTwilioWhatsAppInbox"
      ref="templateParserRef"
      :template="selectedTemplate"
      enable-variable-mapping
    />
    <!-- Twilio Template Display -->
    <div
      v-if="selectedTemplate && isTwilioWhatsAppInbox"
      class="p-4 bg-n-slate-2 rounded-md"
    >
      <div class="flex items-center justify-between mb-2">
        <h4 class="text-sm font-medium text-n-slate-12">
          {{
            selectedTemplate.friendly_name ||
            selectedTemplate.name ||
            'Unnamed Template'
          }}
        </h4>
      </div>
      <div class="text-sm text-n-slate-11 whitespace-pre-wrap mb-2">
        {{
          previewContent ||
          selectedTemplate.body ||
          selectedTemplate.content ||
          'No content available'
        }}
      </div>
      <div class="text-xs text-n-slate-10">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.CATEGORY') }}:
        {{ selectedTemplate.category || 'UTILITY' }}
      </div>

      <!-- Template Variable Mappings -->
      <div
        v-if="templateVariables.length > 0"
        class="mt-4 border-t border-n-weak pt-4"
      >
        <label class="text-sm font-medium text-n-slate-12 mb-3 block">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.VARIABLES_TITLE') }}
        </label>
        <div class="flex flex-col gap-3">
          <div v-for="varKey in templateVariables" :key="varKey">
            <div class="flex items-center gap-3">
              <span
                class="bg-n-alpha-black2 text-n-slate-12 inline-block rounded-md text-xs py-2.5 px-6"
              >
                {{ varKey }}
              </span>
              <ComboBox
                :key="`campaign-var-${varKey}-${variableMappingOptions.length}`"
                :model-value="variableSelections[varKey] || ''"
                :options="variableMappingOptions"
                :placeholder="
                  t(
                    'CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.VARIABLE_SELECT_PLACEHOLDER'
                  )
                "
                appearance="input"
                class="flex-1 [&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
                @update:model-value="val => handleVariableSelect(varKey, val)"
              />
            </div>
            <div
              v-if="isStaticMode(varKey)"
              class="flex items-center gap-3 mt-2"
            >
              <span
                class="invisible inline-block rounded-md text-xs py-2.5 px-6"
              >
                {{ varKey }}
              </span>
              <Input
                v-model="templateVariableMappings[varKey]"
                type="text"
                class="flex-1"
                :placeholder="
                  t(
                    'CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.STATIC_VALUE_PLACEHOLDER'
                  )
                "
              />
            </div>
          </div>
        </div>
        <p class="text-xs text-n-slate-10 mt-2">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.VARIABLE_HINT') }}
        </p>
      </div>
    </div>

    <div class="flex flex-col gap-1">
      <label for="audience" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-text hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
